import React, { useEffect, useState } from 'react';
import {
  FlatList,
  Text,
  TouchableOpacity,
  View,
  ActivityIndicator,
  RefreshControl,
  Alert,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import LinearGradient from 'react-native-linear-gradient';

import { useGetNotifications, Notification } from '~hooks/notifications/useGetNotifications';
import { useMarkAsRead } from '~hooks/notifications/useMarkAsRead';
import { useMarkAllAsRead } from '~hooks/notifications/useMarkAllAsRead';
import { useUnreadNotificationsCount } from '~hooks/notifications/useUnreadNotificationsCount';
import { ModernHeader } from '~components/ModernHeader';
import ModernCard from '~components/ModernCard/ModernCard';
import ModernButton from '~components/ModernButton';
import { SCREENS } from '~constants';
import { NavigationProps } from '~types/navigation/navigation.type';
import { spacing, typography, borderRadius } from '~constants/design';
import { useTheme } from '~contexts/ThemeContext';
import { logScreenView } from '~Utils/firebaseAnalytics';
import Animated, { FadeInDown, ZoomIn } from 'react-native-reanimated';
import { useNotificationContext } from '~contexts/NotificationContext';

const NotificationsScreen: React.FC = () => {
  const { colors } = useTheme();
  const navigation = useNavigation<NavigationProps>();
  const { top } = useSafeAreaInsets();
  const { t } = useTranslation();

  // State to track which notification is being marked as read
  const [markingAsReadId, setMarkingAsReadId] = useState<string | null>(null);

  const { data: notifications, isLoading, isError, refetch, isFetching } = useGetNotifications();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  
  // Also track unread count for real-time updates
  const { refetch: refetchUnreadCount } = useUnreadNotificationsCount();
  const { decrementUnreadCount, resetOptimisticCount, revertOptimisticDecrement } = useNotificationContext();

  // Helper to get notification icon based on type
  const getNotificationIcon = (notification: Notification) => {
    const { notification_type: type } = notification;
    
    if (type?.includes('AdminChat') || type?.includes('Chat')) return '💬';
    if (type?.includes('EVENT_CANCELLED')) return '❌';
    if (type?.includes('EVENT_REQUEST')) return '📋';
    if (type?.includes('EVENT_REVIEW') || type?.includes('RATE_EVENT')) return '⭐';
    if (type?.includes('EVENT_DELETED')) return '🗑️';
    if (type?.includes('REMINDER')) return '🔔';
    if (type?.includes('EVENT_COMMENT')) return '💭';
    if (type?.includes('EVENT_REQUESTING')) return '👥';
    if (type?.includes('SUBSCRIPTION_REMINDER')) return '👑';
    if (type?.includes('EVENT')) return '📅';
    return '📬';
  };

  // Helper to get notification color based on type
  const getNotificationColor = (notification: Notification) => {
    const { notification_type: type } = notification;
    
    if (type?.includes('EVENT_CANCELLED') || type?.includes('EVENT_DELETED')) return colors.error;
    if (type?.includes('EVENT_REMINDER') || type?.includes('EVENT_REVIEW')) return colors.warning || '#FF9800';
    if (type?.includes('AdminChat') || type?.includes('Chat')) return colors.info || '#2196F3';
    return colors.primary;
  };

  // Helper to get gradient colors based on notification type
  const getGradientColors = (notification: Notification) => {
    const baseColor = getNotificationColor(notification);
    return [baseColor + '30', baseColor + '08'];
  };

  useEffect(() => {
    logScreenView('Notifications', 'NotificationsScreen');
  }, []);

  // Refresh unread count when screen comes into focus for real-time updates
  useFocusEffect(
    React.useCallback(() => {
      refetchUnreadCount();
    }, [refetchUnreadCount])
  );

    const handleNotificationClick = (notification: Notification) => {
    console.log('🔍 Notification clicked:', {
      id: notification.id,
      status: notification.status,
      title: notification.title
    });

    // Navigation logic based on notification type (same as FCM implementation)
    const navigateBasedOnType = () => {
      const { notification_type: type, data } = notification;

      console.log('➡️ Navigating based on notification type:', {
        type,
        data
      });
      
      if (data?.chat_id || type === 'AdminChat') {
        // Navigate to chat
        console.log(notification, 'titletitletitletitle');
        
        navigation.navigate(SCREENS.CHAT_STACK, { 
          key: data?.chat_id || '', 
          chatType: type || '',
          userName: notification?.title.replace('Your group chat for this week\'s ', '').replace(' is ready!', '').trim() || '',
        });
      } else if (data?.event_id && (type === 'EVENT_UPDATED' || type === 'EVENT_COMMENT' || 
                                   type === 'EVENT_CANCELLED' || type === 'EVENT_REQUEST_UPDATED' || 
                                   type === 'EVENT_REMINDER')) {
        // Navigate to event detail page
        navigation.navigate(SCREENS.HOME_EVENT, { 
          eventId: Number(data.event_id) 
        });
      } else if (data?.event_id && type === 'EVENT_REVIEW_REMINDER') {
        // Navigate to rate event page
        navigation.navigate(SCREENS.RATE_EVENT, { 
          event: { event_id: data.event_id } 
        });
      } else if (data?.order_id && (type === 'EVENT_DAILY_REMINDER' || 
                                   type === 'EVENT_AFTERNOON_REMINDER' || 
                                   type === 'EVENT_ATTENDANCE_CONFIRMATION')) {
        // Navigate to payment success page for reminder types with order_id
        navigation.navigate(SCREENS.PAYMENT_SUCCESS, { 
          order_id: data.order_id 
        });
      } else if (data?.event_id && type === 'EVENT_REQUESTING') {
        // Navigate to pending attendees
        navigation.navigate(SCREENS.PENDING_ATTENDEES, { 
          eventId: Number(data.event_id), 
          eventName: 'Event' 
        });
      } else if (type === 'EVENT_DELETED') {
        // Do nothing for deleted events
        return;
      } else if (data?.refineSubcategories) {
        // Navigate to settings with refinement
        navigation.navigate(SCREENS.SETTINGS_STACK, { 
          refineSubcategories: data.refineSubcategories 
        });
      } else if (type?.startsWith('EVENT_') && data?.event_id) {
        // Fallback for any other EVENT_ types
        navigation.navigate(SCREENS.HOME_EVENT, { 
          eventId: Number(data.event_id) 
        });
      } else if (type === 'SUBSCRIPTION_REMINDER') {
        navigation.navigate(SCREENS.SUBSCRIPTIONS as never);
      }
    };

    // Check if notification is already read
    if (notification.status.toLowerCase() === 'read') {
      console.log('✅ Notification is already read - navigating without API call');
      // Just navigate without API call for read notifications
      navigateBasedOnType();
      return;
    }

    // Check if this notification is already being marked as read
    if (markingAsReadId === notification.id) {
      console.log('⏳ Notification is already being marked as read - ignoring click');
      return;
    }

    console.log('📤 Notification is unread - marking as read and then navigating');
    
    // Set loading state for this specific notification
    setMarkingAsReadId(notification.id);
    
    // Immediately decrease count for instant UI feedback (optimistic update)
    decrementUnreadCount();
    
    // Mark as read
    markAsRead.mutate(notification.id, {
      onSuccess: () => {
        console.log('✅ Successfully marked notification as read');
        
        // Clear loading state
        setMarkingAsReadId(null);
        
        // Navigate based on notification type
        navigateBasedOnType();
      },
      onError: (error) => {
        console.error('❌ Failed to mark notification as read:', error);
        
        // Clear loading state and revert optimistic update
        setMarkingAsReadId(null);
        revertOptimisticDecrement(); // Revert the optimistic count change
      }
    });
  };

  const handleMarkAllAsRead = () => {
    const unreadNotifications = notifications?.filter(n => n.status.toLowerCase() === 'unread') || [];
    
    if (unreadNotifications.length === 0) {
      Alert.alert(t('notifications.all_read_title', 'All read'), t('notifications.all_read_message', 'All notifications are already marked as read.'));
      return;
    }

    // Immediately reset count for instant UI feedback (optimistic update)
    resetOptimisticCount();

    markAllAsRead.mutate(undefined, {
      onSuccess: () => {
        // No need to refetch since resetOptimisticCount already updated cache
        console.log('✅ Successfully marked all notifications as read');
      },
      onError: (error) => {
        Alert.alert(t('error.title', 'Error'), t('notifications.mark_all_error', 'Failed to mark all notifications as read.'));
        // Revert optimistic update by refetching real count
        refetchUnreadCount();
      }
    });
  };

  const renderNotificationItem = ({ item, index }: { item: Notification; index: number }) => {
    const isUnread = item.status.toLowerCase() === 'unread';
    const isMarkingAsRead = markingAsReadId === item.id;
    const notifColor = getNotificationColor(item);
    const notifIcon = getNotificationIcon(item);
    const gradientColors = getGradientColors(item);
    
    return (
      <Animated.View 
        entering={FadeInDown.delay(index * 40).springify()}
        style={{ marginBottom: spacing.lg, width: '100%' }}
      >
        <TouchableOpacity
          onPress={() => handleNotificationClick(item)}
          disabled={isMarkingAsRead}
          activeOpacity={0.65}
          style={{ width: '100%' }}
        >
          <LinearGradient
            colors={gradientColors}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: borderRadius.xl,
              paddingHorizontal: spacing.md,
              paddingVertical: spacing.md,
              opacity: isMarkingAsRead ? 0.6 : 1,
              borderWidth: isUnread ? 2 : 1,
              borderColor: notifColor + '40',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <View 
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              {/* Icon with premium styling */}
              <View
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 18,
                  backgroundColor: notifColor + '25',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginRight: spacing.lg,
                  borderWidth: 2,
                  borderColor: notifColor + '50',
                  shadowColor: notifColor,
                  shadowOffset: { width: 0, height: 6 },
                  shadowOpacity: 0.25,
                  shadowRadius: 12,
                  elevation: 5,
                  flexShrink: 0,
                }}
              >
                <Text style={{ fontSize: 32 }}>
                  {notifIcon}
                </Text>
              </View>

              {/* Content */}
              <View style={{ flex: 1, marginRight: spacing.md, minWidth: 0 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: spacing.xs }}>
                  <Text
                    style={{
                      fontSize: typography.fontSize.base + 1,
                      fontWeight: isUnread ? '700' : '600',
                      color: isUnread ? colors.textPrimary : colors.textSecondary,
                      flex: 1,
                      letterSpacing: -0.4,
                    }}
                    numberOfLines={2}
                  >
                    {item.title}
                  </Text>
                  {isUnread && (
                    <View
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: notifColor,
                        marginLeft: spacing.sm,
                        shadowColor: notifColor,
                        shadowOffset: { width: 0, height: 3 },
                        shadowOpacity: 0.4,
                        shadowRadius: 6,
                        elevation: 3,
                      }}
                    />
                  )}
                </View>

                {item.body && (
                  <Text
                    style={{
                      fontSize: typography.fontSize.sm + 0.5,
                      fontWeight: '500',
                      color: colors.textSecondary,
                      marginBottom: spacing.lg,
                      lineHeight: typography.fontSize.sm * 1.6,
                      opacity: isUnread ? 0.95 : 0.75,
                      letterSpacing: -0.25,
                    }}
                    numberOfLines={2}
                  >
                    {item.body}
                  </Text>
                )}
                
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                  <Text style={{ fontSize: 11 }}>🕐</Text>
                  <Text
                    style={{
                      fontSize: typography.fontSize.xs,
                      fontWeight: '500',
                      color: colors.textTertiary,
                      opacity: 0.75,
                    }}
                  >
                    {moment(item.created_at).fromNow()}
                  </Text>
                </View>
              </View>

              {/* Right side - Loading indicator or arrow */}
              <View style={{
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 10,
              }}>
                {isMarkingAsRead ? (
                  <ActivityIndicator 
                    size="small" 
                    color={notifColor} 
                  />
                ) : (
                  <Text style={{
                    fontSize: 22,
                    color: notifColor,
                    fontWeight: '800',
                    opacity: isUnread ? 1 : 0.35,
                  }}>
                    ›
                  </Text>
                )}
              </View>
            </View>
          </LinearGradient>
        </TouchableOpacity>
      </Animated.View>
    );
  };

  const renderEmptyState = () => (
    <View style={{
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: spacing.xl,
      paddingVertical: spacing.xxl,
    }}>
      <Animated.View
        entering={ZoomIn.duration(500).springify()}
        style={{
          width: 100,
          height: 100,
          borderRadius: 50,
          backgroundColor: colors.primary + '12',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: spacing.xl,
          borderWidth: 2,
          borderColor: colors.primary + '20',
          shadowColor: colors.primary,
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.15,
          shadowRadius: 16,
          elevation: 4,
        }}
      >
        <Text style={{ fontSize: 50 }}>📭</Text>
      </Animated.View>
      <Text style={{
        fontSize: typography.fontSize.xl,
        fontWeight: '700',
        color: colors.textPrimary,
        textAlign: 'center',
        marginBottom: spacing.md,
        letterSpacing: -0.5,
      }}>
        {t('notifications.empty_title', 'No notifications')}
      </Text>
      <Text style={{
        fontSize: typography.fontSize.base,
        fontWeight: '500',
        color: colors.textSecondary,
        textAlign: 'center',
        lineHeight: typography.fontSize.base * 1.6,
      }}>
        {t('notifications.empty_message', 'When you receive notifications, they will appear here.')}
      </Text>
    </View>
  );

  const renderError = () => (
    <View style={{
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: spacing.xl,
    }}>
      <Animated.View
        entering={ZoomIn.duration(500).springify()}
        style={{
          width: 100,
          height: 100,
          borderRadius: 50,
          backgroundColor: (colors.error || '#FF6B6B') + '12',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: spacing.xl,
          borderWidth: 2,
          borderColor: (colors.error || '#FF6B6B') + '25',
          shadowColor: colors.error,
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.15,
          shadowRadius: 16,
          elevation: 4,
        }}
      >
        <Text style={{ fontSize: 50 }}>⚠️</Text>
      </Animated.View>
      <Text style={{
        fontSize: typography.fontSize.xl,
        fontWeight: '700',
        color: colors.error,
        textAlign: 'center',
        marginBottom: spacing.md,
        letterSpacing: -0.5,
      }}>
        {t('error.title', 'Error')}
      </Text>
      <Text style={{
        fontSize: typography.fontSize.base,
        fontWeight: '500',
        color: colors.textSecondary,
        textAlign: 'center',
        marginBottom: spacing.xl,
        lineHeight: typography.fontSize.base * 1.6,
      }}>
        {t('notifications.load_error', 'Failed to load notifications. Please try again.')}
      </Text>
      <ModernButton
        title={t('common.retry', 'Retry')}
        onPress={() => refetch()}
        variant="primary"
      />
    </View>
  );

  if (isError) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.background, paddingTop: top }}>
        <ModernHeader
          title={t('notifications', 'Notifications')}
          showBackButton
          onBackPress={() => navigation.goBack()}
        />
        {renderError()}
      </View>
    );
  }

  const hasUnreadNotifications = notifications?.some(n => n.status.toLowerCase() === 'unread') || false;

  return (
    <View style={{ flex: 1, backgroundColor: colors.background, paddingTop: top }}>
      <ModernHeader
        title={t('notifications', 'Notifications')}
        showBackButton
        onBackPress={() => navigation.goBack()}
      />

      {hasUnreadNotifications && (
        <View
          entering={FadeInDown.duration(400).springify()}
          style={{
            paddingHorizontal: spacing.lg,
            paddingVertical: spacing.md,
          }}
        >
          <LinearGradient
            colors={[colors.primary, colors.primary + 'DD']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: borderRadius.xl,
              overflow: 'hidden',
              shadowColor: colors.primary,
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.3,
              shadowRadius: 16,
              elevation: 8,
            }}
          >
            <TouchableOpacity
              onPress={handleMarkAllAsRead}
              disabled={markAllAsRead.isLoading}
              activeOpacity={0.85}
              style={{
                paddingHorizontal: spacing.xl,
                paddingVertical: spacing.md + 4,
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              {markAllAsRead.isLoading ? (
                <ActivityIndicator size="small" color="#fff" style={{ marginRight: spacing.sm }} />
              ) : (
                <Text style={{
                  fontSize: 18,
                  fontWeight: '800',
                  color: '#fff',
                  marginRight: spacing.sm,
                }}>
                  ✓
                </Text>
              )}
              <Text style={{
                fontSize: typography.fontSize.base + 1,
                fontWeight: '700',
                color: '#fff',
                letterSpacing: -0.3,
              }}>
                {t('notifications.mark_all_read', 'Mark all read')}
              </Text>
            </TouchableOpacity>
          </LinearGradient>
        </View>
      )}

      {isLoading ? (
        <View style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <View
            style={{
              width: 70,
              height: 70,
              borderRadius: 35,
              backgroundColor: colors.primary + '12',
              justifyContent: 'center',
              alignItems: 'center',
              borderWidth: 2,
              borderColor: colors.primary + '20',
              shadowColor: colors.primary,
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.15,
              shadowRadius: 16,
              elevation: 4,
            }}
          >
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        </View>
      ) : (
        <FlatList
          data={notifications || []}
          renderItem={renderNotificationItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={{
            flexGrow: 1,
            paddingVertical: spacing.lg,
            paddingBottom: spacing.xxl,
            paddingHorizontal: spacing.md,
            maxWidth: 520,
            width: '100%',
            marginHorizontal: 'auto',
            alignSelf: 'center',
          }}
          ListEmptyComponent={renderEmptyState}
          scrollIndicatorInsets={{ right: 1 }}
          refreshControl={
            <RefreshControl
              refreshing={isFetching}
              onRefresh={refetch}
              tintColor={colors.primary}
            />
          }
          showsVerticalScrollIndicator={false}
          style={{ width: '100%' }}
          nestedScrollEnabled={true}
        />
      )}
    </View>
  );
};

export default NotificationsScreen;